import { getTradeTerminology } from "../utils/tradeTerminology";

/** Mirrors backend — sales point POS view: restaurant vs simple shop billing */

export const WORK_CATEGORIES = {
  RESTAURANT: "restaurant",
  SIMPLE: "simple",
} as const;

export type WorkCategory =
  (typeof WORK_CATEGORIES)[keyof typeof WORK_CATEGORIES];

export const WORK_CATEGORY_VALUES = Object.values(WORK_CATEGORIES);

export const WORK_CATEGORY_OPTIONS: { value: WorkCategory; label: string }[] = [
  { value: "restaurant", label: "Restaurant (Dining & Tables)" },
  {
    value: "simple",
    label: "Retail & General Billing",
  },
];

export const WORK_CATEGORY_LABELS: Record<WorkCategory, string> = {
  restaurant: "Restaurant (Dining & Tables)",
  simple: "Retail & General Billing",
};

export function getWorkCategoryOptions(businessType?: string | null): { value: WorkCategory; label: string }[] {
  const terms = getTradeTerminology(businessType);
  return [
    { value: "restaurant", label: "Restaurant (Dining & Tables)" },
    { value: "simple", label: terms.workCategorySimpleLabel },
  ];
}

export function normalizeWorkCategory(value?: string | null): WorkCategory {
  const v = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!v || v === "restaurant") return WORK_CATEGORIES.RESTAURANT;
  return WORK_CATEGORIES.SIMPLE;
}

export function workCategoryLabel(value?: string | null, businessType?: string | null): string {
  if (value == null || String(value).trim() === "") return "—";
  const norm = normalizeWorkCategory(value);
  if (norm === WORK_CATEGORIES.RESTAURANT) {
    return "Restaurant (Dining & Tables)";
  }
  if (businessType) {
    return getTradeTerminology(businessType).workCategorySimpleLabel;
  }
  const storedType = typeof localStorage !== "undefined" ? localStorage.getItem("business_type") : null;
  if (storedType) {
    return getTradeTerminology(storedType).workCategorySimpleLabel;
  }
  return WORK_CATEGORY_LABELS.simple;
}

/** Dedupe + map legacy tags (grocery, medical, …) → restaurant | simple */
export function uniqueNormalizedWorkCategories(
  values?: Array<string | null | undefined> | null
): WorkCategory[] {
  if (!Array.isArray(values) || !values.length) return [];
  const seen = new Set<WorkCategory>();
  const out: WorkCategory[] = [];
  for (const value of values) {
    const next = normalizeWorkCategory(value);
    if (seen.has(next)) continue;
    seen.add(next);
    out.push(next);
  }
  return out;
}
